import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { BackButton, FormSection, InlineFeedback, PrimaryButton, SecondaryButton } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useState } from 'react';
import type { MetaConnection } from 'services/api/relifMeta.api';
import {
  useGetMetaPagesQuery,
  useSubscribeMetaPageMutation,
  useUnsubscribeMetaPageMutation,
} from 'services/api/relifMeta.api';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import ConnectionGate from './_shared/ConnectionGate';
import { friendlyError } from './_shared/utils';

interface FormsTabProps {
  businessUnitId: number;
  connection: MetaConnection | null;
  isLoading: boolean;
  onSwitchToConnection: () => void;
}

const FormsTab: React.FC<FormsTabProps> = ({ businessUnitId, connection, isLoading, onSwitchToConnection }) => {
  const { notifySaved } = useFeedback();
  const skip = !connection;
  const queryArg = connection ? { connectionId: connection.id, businessUnitId } : { connectionId: 0 };
  const {
    data,
    isLoading: pagesLoading,
    isFetching,
    error: pagesError,
    refetch,
  } = useGetMetaPagesQuery(queryArg, { skip });

  const [subscribeMetaPage, { isLoading: isSubscribing }] = useSubscribeMetaPageMutation();
  const [unsubscribeMetaPage, { isLoading: isUnsubscribing }] = useUnsubscribeMetaPageMutation();

  const [pendingPageId, setPendingPageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (!connection) {
    return <ConnectionGate onSwitch={onSwitchToConnection} surface="forms" />;
  }

  const pages = data?.pages ?? [];

  const handleAssign = async (metaPageId: string) => {
    setError(null);
    setPendingPageId(metaPageId);
    try {
      await subscribeMetaPage({ metaConnectionId: connection.id, metaPageId, businessUnitId }).unwrap();
      await refetch();
      notifySaved('Página suscrita');
    } catch (err) {
      setError(friendlyError(err, 'No pudimos suscribir la página.'));
    } finally {
      setPendingPageId(null);
    }
  };

  const handleUnassign = async (metaPageId: string) => {
    setError(null);
    setPendingPageId(metaPageId);
    try {
      await unsubscribeMetaPage(metaPageId).unwrap();
      await refetch();
      notifySaved('Página desuscrita');
    } catch (err) {
      setError(friendlyError(err, 'No pudimos desuscribir la página.'));
    } finally {
      setPendingPageId(null);
    }
  };

  return (
    <Stack spacing={3}>
      {error && <InlineFeedback severity="error">{error}</InlineFeedback>}
      {pagesError && (
        <InlineFeedback severity="error">No pudimos cargar las páginas. Refresca para reintentar.</InlineFeedback>
      )}

      <FormSection
        eyebrow="Formularios de leads"
        title="Páginas de Facebook que envían leads a esta BU"
        description="Las páginas se suscriben automáticamente al conectar Meta. Aquí solo gestionas excepciones — reasignar o desuscribir cuando algo cambia."
      >
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end', mb: 1.5 }}>
          <BackButton onClick={() => refetch().catch(() => undefined)} disabled={isFetching}>
            {isFetching ? 'Refrescando…' : 'Refrescar'}
          </BackButton>
        </Stack>

        {pagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: NAVY }} />
          </Box>
        ) : pages.length === 0 ? (
          <Box
            sx={{ backgroundColor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, py: 6, textAlign: 'center' }}
          >
            <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.55), fontSize: '0.95rem' }}>
              Sin páginas disponibles para esta unidad de negocio.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{ backgroundColor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, overflow: 'hidden' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <HeadCell>Página</HeadCell>
                  <HeadCell>Meta Page ID</HeadCell>
                  <HeadCell>Estado</HeadCell>
                  <HeadCell align="right">Acción</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pages.map((page) => {
                  const assignedToCurrent = page.businessUnitId === businessUnitId;
                  const isRowPending = pendingPageId === page.metaPageId && (isSubscribing || isUnsubscribing);

                  return (
                    <BodyRow key={page.metaPageId}>
                      <BodyCell weight={600}>{page.pageName}</BodyCell>
                      <BodyCell mono>{page.metaPageId}</BodyCell>
                      <BodyCell>
                        {page.isSubscribedToLeadgen && assignedToCurrent ? (
                          <StatusPill tone="good" label="Suscrita" />
                        ) : assignedToCurrent ? (
                          <StatusPill tone="warn" label="Asignada sin Leadgen" />
                        ) : page.businessUnitId === null ? (
                          <StatusPill tone="neutral" label="Sin asignar" />
                        ) : (
                          <StatusPill tone="neutral" label="Otra BU" />
                        )}
                      </BodyCell>
                      <BodyCell align="right">
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          {!assignedToCurrent && (
                            <PrimaryButton
                              size="small"
                              disabled={isRowPending}
                              onClick={() => {
                                handleAssign(page.metaPageId).catch(() => undefined);
                              }}
                            >
                              {isRowPending ? 'Suscribiendo…' : 'Suscribir'}
                            </PrimaryButton>
                          )}
                          {assignedToCurrent && page.isSubscribedToLeadgen && (
                            <SecondaryButton
                              size="small"
                              disabled={isRowPending}
                              onClick={() => {
                                handleUnassign(page.metaPageId).catch(() => undefined);
                              }}
                              sx={{ bgcolor: 'transparent', color: NAVY, '&:hover': { bgcolor: alpha(NAVY, 0.05) } }}
                            >
                              {isRowPending ? 'Desuscribiendo…' : 'Desuscribir'}
                            </SecondaryButton>
                          )}
                        </Stack>
                      </BodyCell>
                    </BodyRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </FormSection>
    </Stack>
  );
};

const HeadCell: React.FC<{ children: React.ReactNode; align?: 'right' }> = ({ children, align }) => (
  <TableCell
    align={align}
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOW,
      border: 'none',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: alpha(NAVY, 0.55),
      py: 1.5,
    }}
  >
    {children}
  </TableCell>
);

const BodyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TableRow
    sx={{
      transition: 'background-color 0.18s ease',
      '&:hover': { backgroundColor: alpha(NAVY, 0.03) },
    }}
  >
    {children}
  </TableRow>
);

const BodyCell: React.FC<{ children: React.ReactNode; mono?: boolean; weight?: number; align?: 'right' }> = ({
  children,
  mono,
  weight = 500,
  align,
}) => (
  <TableCell
    align={align}
    sx={{
      border: 'none',
      fontFamily: mono ? '"JetBrains Mono", monospace' : 'Jost, sans-serif',
      fontSize: '0.9375rem',
      fontWeight: weight,
      color: NAVY,
      py: 1.75,
    }}
  >
    {children}
  </TableCell>
);

const PILL_TONES = {
  good: { dot: '#53B721', text: '#1F8A3D' },
  warn: { dot: '#CF5C09', text: '#A6450B' },
  neutral: { dot: alpha(NAVY, 0.4), text: alpha(NAVY, 0.55) },
} as const;

const StatusPill: React.FC<{ tone: keyof typeof PILL_TONES; label: string }> = ({ tone, label }) => {
  const colors = PILL_TONES[tone];
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.dot }} />
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: colors.text,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

export default FormsTab;
